export interface LoincCode {
  code: string;
  display: string;
  unit: string;
  /** UCUM code */
  unitCode: string;
  category: "vital-signs" | "laboratory";
  valueRange: { min: number; max: number };
  /** Decimal places to round the generated value to (0 = integer) */
  decimals: number;
}

export const COMMON_LOINC_CODES: LoincCode[] = [
  // Vital signs
  {
    code: "8867-4",
    display: "Heart rate",
    unit: "beats/minute",
    unitCode: "/min",
    category: "vital-signs",
    valueRange: { min: 50, max: 120 },
    decimals: 0,
  },
  {
    code: "8310-5",
    display: "Body temperature",
    unit: "degrees Celsius",
    unitCode: "Cel",
    category: "vital-signs",
    valueRange: { min: 35.5, max: 39.0 },
    decimals: 1,
  },
  {
    code: "8480-6",
    display: "Systolic blood pressure",
    unit: "mmHg",
    unitCode: "mm[Hg]",
    category: "vital-signs",
    valueRange: { min: 90, max: 180 },
    decimals: 0,
  },
  {
    code: "8462-4",
    display: "Diastolic blood pressure",
    unit: "mmHg",
    unitCode: "mm[Hg]",
    category: "vital-signs",
    valueRange: { min: 50, max: 110 },
    decimals: 0,
  },
  {
    code: "9279-1",
    display: "Respiratory rate",
    unit: "breaths/minute",
    unitCode: "/min",
    category: "vital-signs",
    valueRange: { min: 10, max: 30 },
    decimals: 0,
  },
  {
    code: "2708-6",
    display: "Oxygen saturation",
    unit: "%",
    unitCode: "%",
    category: "vital-signs",
    valueRange: { min: 88, max: 100 },
    decimals: 0,
  },
  {
    code: "29463-7",
    display: "Body weight",
    unit: "kg",
    unitCode: "kg",
    category: "vital-signs",
    valueRange: { min: 40, max: 150 },
    decimals: 1,
  },
  {
    code: "8302-2",
    display: "Body height",
    unit: "cm",
    unitCode: "cm",
    category: "vital-signs",
    valueRange: { min: 140, max: 200 },
    decimals: 0,
  },
  // Laboratory
  {
    code: "2339-0",
    display: "Glucose",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: "laboratory",
    valueRange: { min: 60, max: 200 },
    decimals: 0,
  },
  {
    code: "718-7",
    display: "Hemoglobin",
    unit: "g/dL",
    unitCode: "g/dL",
    category: "laboratory",
    valueRange: { min: 8, max: 18 },
    decimals: 1,
  },
  {
    code: "4548-4",
    display: "HbA1c",
    unit: "%",
    unitCode: "%",
    category: "laboratory",
    valueRange: { min: 4, max: 14 },
    decimals: 1,
  },
  {
    code: "2160-0",
    display: "Creatinine",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: "laboratory",
    valueRange: { min: 0.5, max: 2.5 },
    decimals: 1,
  },
  {
    code: "6690-2",
    display: "WBC count",
    unit: "10*3/uL",
    unitCode: "10*3/uL",
    category: "laboratory",
    valueRange: { min: 3, max: 15 },
    decimals: 1,
  },
  // Additional laboratory codes
  {
    code: "2951-2",
    display: "Sodium",
    unit: "mmol/L",
    unitCode: "mmol/L",
    category: "laboratory",
    valueRange: { min: 130, max: 150 },
    decimals: 0,
  },
  {
    code: "2823-3",
    display: "Potassium",
    unit: "mmol/L",
    unitCode: "mmol/L",
    category: "laboratory",
    valueRange: { min: 3.0, max: 5.5 },
    decimals: 1,
  },
  {
    code: "17861-6",
    display: "Calcium",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: "laboratory",
    valueRange: { min: 8.0, max: 11.0 },
    decimals: 1,
  },
  {
    code: "1751-7",
    display: "Albumin",
    unit: "g/dL",
    unitCode: "g/dL",
    category: "laboratory",
    valueRange: { min: 2.5, max: 5.0 },
    decimals: 1,
  },
  {
    code: "2885-2",
    display: "Total protein",
    unit: "g/dL",
    unitCode: "g/dL",
    category: "laboratory",
    valueRange: { min: 5.5, max: 9.0 },
    decimals: 1,
  },
  {
    code: "3016-3",
    display: "TSH",
    unit: "mIU/L",
    unitCode: "mIU/L",
    category: "laboratory",
    valueRange: { min: 0.3, max: 5.0 },
    decimals: 2,
  },
  {
    code: "2093-3",
    display: "Total cholesterol",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: "laboratory",
    valueRange: { min: 100, max: 300 },
    decimals: 0,
  },
  {
    code: "18262-6",
    display: "LDL cholesterol",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: "laboratory",
    valueRange: { min: 50, max: 200 },
    decimals: 0,
  },
  {
    code: "2085-9",
    display: "HDL cholesterol",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: "laboratory",
    valueRange: { min: 20, max: 100 },
    decimals: 0,
  },
  {
    code: "2571-8",
    display: "Triglycerides",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: "laboratory",
    valueRange: { min: 50, max: 400 },
    decimals: 0,
  },
  {
    code: "1742-6",
    display: "ALT",
    unit: "U/L",
    unitCode: "U/L",
    category: "laboratory",
    valueRange: { min: 5, max: 100 },
    decimals: 0,
  },
  {
    code: "1920-8",
    display: "AST",
    unit: "U/L",
    unitCode: "U/L",
    category: "laboratory",
    valueRange: { min: 5, max: 100 },
    decimals: 0,
  },
  {
    code: "1975-2",
    display: "Bilirubin total",
    unit: "mg/dL",
    unitCode: "mg/dL",
    category: "laboratory",
    valueRange: { min: 0.1, max: 3.0 },
    decimals: 1,
  },
  {
    code: "777-3",
    display: "Platelet count",
    unit: "10*3/uL",
    unitCode: "10*3/uL",
    category: "laboratory",
    valueRange: { min: 100, max: 500 },
    decimals: 0,
  },
  {
    code: "62238-1",
    display: "eGFR",
    unit: "mL/min/1.73m2",
    unitCode: "mL/min/{1.73_m2}",
    category: "laboratory",
    valueRange: { min: 15, max: 120 },
    decimals: 0,
  },
  // Additional vital-signs codes
  {
    code: "59408-5",
    display: "Oxygen saturation by pulse oximetry",
    unit: "%",
    unitCode: "%",
    category: "vital-signs",
    valueRange: { min: 88, max: 100 },
    decimals: 0,
  },
  {
    code: "8287-5",
    display: "Head circumference",
    unit: "cm",
    unitCode: "cm",
    category: "vital-signs",
    valueRange: { min: 50, max: 62 },
    decimals: 0,
  },
];
